// Правка карточки человека. Панель, а не отдельный экран: список слева остаётся на месте, и
// адрес с открытой панелью пересылается коллеге, как у всех остальных мастеров раздела.
//
// ## Почему этой панели не было до 06.09.2026
//
// Маршрут правки на сервере есть с первого дня, а звать его было НЕКОМУ: ни одна поверхность
// кабинета его не вызывала. Опечатку в фамилии, новый телефон, переезд в другой город и тип
// оформления исправить было нечем — карточка заводилась один раз и навсегда. На приёмке это
// первое, что пробуют: заказчик заводит человека, видит опечатку и ищет, где её поправить.
//
// ## Что здесь НЕ правится и почему
//
// * **Статус** — у него свой мастер (`StatusPanel`) с историей и датой: смена статуса это
//   событие, а не поле. Увольнение и вовсе идёт отдельным путём со своим правом и причиной.
// * **Паспорт** — заводится один раз при создании и хранится зашифрованным; правка документа
//   это новый документ во вкладке «Документы», а не переписанное поле.
// * **Дата приёма** правится: у заказчика она в половине карточек проставлена задним числом и
//   ошибается, а от неё считается первая вахта и правило билета.
//
// Пустая строка означает «стереть значение», и это сказано подсказкой: у сервера `null` и
// «поле не прислали» — разные вещи, и по ним расходится результат.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine, RiPhoneLine, RiUser3Line } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import type { EmployeePatch } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { useEmploymentTypesQuery } from '@/shared/org/queries';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { useEmployeeQuery, usePatchEmployeeMutation } from '../queries';

/** То же право, что у заведения: правка карточки — это та же работа с реестром людей. */
const EDIT_PERMISSION = 'core.employee.edit';

/** Пусто на экране означает «стереть»; сервер различает `null` и отсутствие ключа. */
function илиПусто(значение: string): string | null {
  const очищенное = значение.trim();
  return очищенное === '' ? null : очищенное;
}

export function EditEmployeePanel({ id, onClose }: { id: string; onClose: () => void }) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, EDIT_PERMISSION);
  const карточка = useEmployeeQuery(id);
  const правка = usePatchEmployeeMutation(id);

  // Поля наполняются из карточки один раз, когда она приехала. Держать их в `useEffect` и
  // переписывать на каждый ответ сервера нельзя: обновление списка затёрло бы то, что человек
  // уже набрал, и он бы этого не заметил.
  const [черновик, setЧерновик] = useState<{
    фамилия: string;
    имя: string;
    отчество: string;
    телефон: string;
    второйТелефон: string;
    город: string;
    табельный: string;
    тип: string;
    принят: string;
  } | null>(null);
  const [ошибки, setОшибки] = useState<Partial<Record<'фамилия' | 'имя', string>>>({});
  const [заслон, setЗаслон] = useState<string | null>(null);

  const типы = useEmploymentTypesQuery();

  if (карточка.data === undefined) {
    return (
      <div className='flex flex-col gap-3'>
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-2/3' />
      </div>
    );
  }

  const человек = карточка.data;
  const поля = черновик ?? {
    фамилия: человек.last_name,
    имя: человек.first_name,
    отчество: человек.middle_name ?? '',
    телефон: человек.phone_normalized ?? '',
    второйТелефон: человек.phone_extra ?? '',
    город: человек.city ?? '',
    табельный: человек.personnel_no ?? '',
    тип: человек.employment_type_id ?? NO_VALUE,
    принят: человек.hired_at ?? '',
  };

  function поставить(ключ: keyof typeof поля, значение: string) {
    setЧерновик({ ...поля, [ключ]: значение });
  }

  function сохранить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!можно) {
      setЗаслон(
        'Править карточки может тот, у кого есть право на реестр людей. Попросите администратора выдать его — набранное никуда не денется.',
      );
      return;
    }
    if (правка.isPending) {
      setЗаслон('Карточка уже сохраняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const беда: Partial<Record<'фамилия' | 'имя', string>> = {};
    if (поля.фамилия.trim() === '') {
      беда.фамилия = 'Фамилию стереть нельзя: по ней человека находят в реестре и в ведомости.';
    }
    if (поля.имя.trim() === '') {
      беда.имя = 'Имя стереть нельзя: им человек подписывается в ведомости.';
    }
    if (Object.keys(беда).length > 0) {
      setОшибки(беда);
      return;
    }

    setОшибки({});
    setЗаслон(null);
    const тело: EmployeePatch = {
      last_name: поля.фамилия.trim(),
      first_name: поля.имя.trim(),
      middle_name: илиПусто(поля.отчество),
      phone: илиПусто(поля.телефон),
      phone_extra: илиПусто(поля.второйТелефон),
      city: илиПусто(поля.город),
      personnel_no: илиПусто(поля.табельный),
      employment_type_id: поля.тип === NO_VALUE ? null : поля.тип,
      hired_at: поля.принят === '' ? null : поля.принят,
    };
    правка.mutate(тело, { onSuccess: onClose });
  }

  return (
    <form onSubmit={сохранить} noValidate className='flex flex-col gap-3'>
      {!можно && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку карточек у вас нет: форму можно заполнить, но сохранить не выйдет.
            Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <TextField
        id='edit-employee-last-name'
        label='Фамилия'
        required
        icon={RiUser3Line}
        value={поля.фамилия}
        onChange={(значение) => поставить('фамилия', значение)}
        error={ошибки.фамилия}
      />
      <TextField
        id='edit-employee-first-name'
        label='Имя'
        required
        value={поля.имя}
        onChange={(значение) => поставить('имя', значение)}
        error={ошибки.имя}
      />
      <TextField
        id='edit-employee-middle-name'
        label='Отчество'
        value={поля.отчество}
        onChange={(значение) => поставить('отчество', значение)}
        hint='Пусто — значит отчества нет в документах, и оно будет стёрто.'
      />

      <TextField
        id='edit-employee-phone'
        label='Телефон'
        icon={RiPhoneLine}
        type='tel'
        inputMode='tel'
        value={поля.телефон}
        onChange={(значение) => поставить('телефон', значение)}
        hint='Пишите как удобно: сервер приведёт номер к единому виду.'
      />
      <TextField
        id='edit-employee-phone-extra'
        label='Второй телефон'
        type='tel'
        inputMode='tel'
        value={поля.второйТелефон}
        onChange={(значение) => поставить('второйТелефон', значение)}
        hint='Родственник или второй номер самого человека: на вахте первый бывает вне сети.'
      />

      <TextField
        id='edit-employee-city'
        label='Город'
        value={поля.город}
        onChange={(значение) => поставить('город', значение)}
        hint='Город человека, а не объекта: по нему считают дорогу на вахту.'
      />
      <TextField
        id='edit-employee-personnel-no'
        label='Табельный номер'
        value={поля.табельный}
        onChange={(значение) => поставить('табельный', значение)}
        hint='Номер из кадровой системы заказчика. Без него в карточке стоит «не присвоен».'
      />

      <SelectField
        id='edit-employee-type'
        label='Тип оформления'
        value={поля.тип}
        onChange={(значение) => поставить('тип', значение)}
        options={(типы.data?.items ?? []).map((строка) => ({
          value: строка.id,
          label: строка.title,
        }))}
        noneLabel='Тип не задан'
        placeholder='Выберите тип оформления'
        pending={типы.isPending}
        failed={типы.isError}
        failedError={типы.error as ApiError | null}
        emptyNote='Типов оформления в справочнике нет. Их заводит администратор в настройках; без типа человек не попадёт ни в одно представление реестра по оформлению.'
      />

      <DateField
        id='edit-employee-hired-at'
        label='Дата приёма'
        value={поля.принят}
        onChange={(значение) => поставить('принят', значение)}
        hint='С неё считается стаж и первая вахта — а от первой вахты зависит компенсация билета.'
      />

      {правка.isError && <ErrorAlert error={правка.error} />}

      {заслон !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {заслон}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {правка.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
